import React, {
  useMemo,
  useState
} from 'react'

import axios from 'axios'

import {
  Link,
  useNavigate,
  useParams
} from 'react-router-dom'

import { useSelector } from 'react-redux'

import logo from '../../logo.png'

import {
  FiCheck,
  FiEye,
  FiEyeOff,
  FiLock,
  FiShield
} from 'react-icons/fi'


export default function ChangePassword() {

  const { token } =
    useParams()

  const navigate =
    useNavigate()

  const { url } =
    useSelector(
      state => state
    )


  const [password, setPassword] =
    useState('')

  const [
    confirmPassword,
    setConfirmPassword
  ] = useState('')

  const [
    showPassword,
    setShowPassword
  ] = useState(false)

  const [
    showConfirmPassword,
    setShowConfirmPassword
  ] = useState(false)

  const [loading, setLoading] =
    useState(false)

  const [error, setError] =
    useState('')

  const [success, setSuccess] =
    useState(false)



  const passwordChecks =
    useMemo(() => {

      return {
        length:
          password.length >= 8,

        uppercase:
          /[A-Z]/.test(password),

        lowercase:
          /[a-z]/.test(password),

        number:
          /[0-9]/.test(password),

        symbol:
          /[^A-Za-z0-9]/.test(password)
      }

    }, [password])


  const passwordValid =
    Object
      .values(passwordChecks)
      .every(Boolean)



  const submit = async (
    event
  ) => {

    event.preventDefault()

    setError('')


    if (!token) {

      setError(
        'This password reset link is invalid.'
      )

      return
    }


    if (!passwordValid) {

      setError(
        'Please make sure your password meets all requirements.'
      )

      return
    }


    if (
      password !==
      confirmPassword
    ) {

      setError(
        'Your passwords do not match.'
      )

      return
    }


    try {

      setLoading(true)


      const formData =
        new FormData()

      formData.append(
        'password',
        password
      )


      const response =
        await axios.post(
          `${url}/auth/change-password/${token}`,
          formData
        )


      if (
        response.data.status === 200
      ) {
          localStorage.removeItem(
          'access_token'
        )

        setSuccess(true)


        setTimeout(() => {

          navigate(
            '/auth/signin/'
          )

        }, 2500)


        return
      }

      setError(
        response.data.error ||
        'Unable to change your password.'
      )


    } catch (err) {

      setError(
        err.response?.data?.detail ||
        err.response?.data?.error ||
        'Unable to change your password. The reset link may have expired.'
      )

    } finally {

      setLoading(false)
    }
  }



  return (

    <div className="
      min-h-screen
      grid
      grid-cols-1
      lg:grid-cols-2
      font-[Satoshi]
    ">


      {/* LEFT PANEL */}

      <div className="
        hidden
        lg:flex
        bg-[#101828]
        text-white
        px-14
        xl:px-20
        py-14
        flex-col
        justify-between
      ">


        <div className="
          flex
          items-center
          gap-3
        ">

          <div className="
            w-11
            h-11
            rounded-xl
            overflow-hidden
            bg-white
          ">

            <img
              src={logo}
              alt="RentHub"
              className="
                w-full
                h-full
                object-cover
              "
            />

          </div>


          <span className="
            text-xl
            font-bold
          ">
            RentHub
          </span>

        </div>



        <div className="
          max-w-lg
        ">

          <div className="
            w-12
            h-12
            rounded-xl
            bg-green-500/10
            text-green-400
            flex
            items-center
            justify-center
            text-xl
            mb-7
          ">
            <FiLock />
          </div>


          <h1 className="
            text-4xl
            xl:text-5xl
            font-bold
            leading-tight
          ">
            Secure your account.
          </h1>


          <p className="
            text-gray-400
            leading-7
            mt-5
          ">
            Choose a strong new password
            for your RentHub account.
          </p>

        </div>



        <div className="
          flex
          items-center
          gap-2
          text-sm
          text-gray-400
        ">

          <FiShield className="
            text-green-400
          " />

          Secure password recovery

        </div>

      </div>



      {/* FORM */}

      <div className="
        bg-white
        flex
        items-center
        justify-center
        px-5
        py-10
        sm:px-8
      ">

        <div className="
          w-full
          max-w-md
        ">


          <Link
            to="/"
            className="
              lg:hidden
              inline-flex
              items-center
              gap-3
              mb-10
            "
          >

            <div className="
              w-10
              h-10
              rounded-xl
              overflow-hidden
            ">

              <img
                src={logo}
                alt="RentHub"
                className="
                  w-full
                  h-full
                  object-cover
                "
              />

            </div>


            <span className="
              text-xl
              font-bold
            ">
              RentHub
            </span>

          </Link>



          {success ? (

            <div className="
              text-center
            ">

              <div className="
                w-14
                h-14
                rounded-full
                bg-green-50
                text-green-600
                flex
                items-center
                justify-center
                mx-auto
                text-2xl
              ">
                <FiCheck />
              </div>


              <h2 className="
                text-2xl
                font-bold
                mt-6
              ">
                Password changed
              </h2>


              <p className="
                text-sm
                text-gray-500
                leading-6
                mt-3
              ">
                Your password has been
                updated successfully.
                You're being redirected
                to sign in.
              </p>

            </div>

          ) : (

            <>

              <h2 className="
                text-3xl
                font-bold
                tracking-tight
              ">
                Create a new password
              </h2>


              <p className="
                text-gray-500
                mt-2
              ">
                Your new password must
                meet the security
                requirements below.
              </p>


              <form
                onSubmit={submit}
                className="mt-8"
              >


                {/* PASSWORD */}

                <label
                  htmlFor="password"
                  className="
                    block
                    text-sm
                    font-semibold
                    text-gray-700
                    mb-2
                  "
                >
                  New password
                </label>


                <div className="
                  relative
                ">

                  <FiLock className="
                    absolute
                    left-4
                    top-1/2
                    -translate-y-1/2
                    text-gray-400
                  " />


                  <input
                    id="password"
                    type={
                      showPassword
                        ? 'text'
                        : 'password'
                    }
                    value={password}
                    onChange={
                      event =>
                        setPassword(
                          event.target.value
                        )
                    }
                    autoComplete="new-password"
                    className="
                      w-full
                      h-12
                      border
                      border-gray-300
                      rounded-xl
                      pl-11
                      pr-12
                      outline-none
                      focus:border-green-500
                      focus:ring-2
                      focus:ring-green-100
                    "
                    placeholder="Enter new password"
                  />


                  <button
                    type="button"
                    onClick={() =>
                      setShowPassword(
                        current => !current
                      )
                    }
                    className="
                      absolute
                      right-4
                      top-1/2
                      -translate-y-1/2
                      text-gray-400
                      hover:text-gray-600
                    "
                  >

                    {
                      showPassword
                        ? <FiEyeOff />
                        : <FiEye />
                    }

                  </button>

                </div>



                {/* REQUIREMENTS */}

                <div className="
                  grid
                  grid-cols-2
                  gap-x-4
                  gap-y-2
                  mt-4
                ">

                  <Requirement
                    valid={
                      passwordChecks.length
                    }
                    text="8+ characters"
                  />

                  <Requirement
                    valid={
                      passwordChecks.uppercase
                    }
                    text="Uppercase letter"
                  />

                  <Requirement
                    valid={
                      passwordChecks.lowercase
                    }
                    text="Lowercase letter"
                  />

                  <Requirement
                    valid={
                      passwordChecks.number
                    }
                    text="Number"
                  />

                  <Requirement
                    valid={
                      passwordChecks.symbol
                    }
                    text="Symbol"
                  />

                </div>



                {/* CONFIRM PASSWORD */}

                <label
                  htmlFor="confirmPassword"
                  className="
                    block
                    text-sm
                    font-semibold
                    text-gray-700
                    mt-7
                    mb-2
                  "
                >
                  Confirm password
                </label>


                <div className="
                  relative
                ">

                  <FiLock className="
                    absolute
                    left-4
                    top-1/2
                    -translate-y-1/2
                    text-gray-400
                  " />


                  <input
                    id="confirmPassword"
                    type={
                      showConfirmPassword
                        ? 'text'
                        : 'password'
                    }
                    value={confirmPassword}
                    onChange={
                      event =>
                        setConfirmPassword(
                          event.target.value
                        )
                    }
                    autoComplete="new-password"
                    className="
                      w-full
                      h-12
                      border
                      border-gray-300
                      rounded-xl
                      pl-11
                      pr-12
                      outline-none
                      focus:border-green-500
                      focus:ring-2
                      focus:ring-green-100
                    "
                    placeholder="Enter password again"
                  />


                  <button
                    type="button"
                    onClick={() =>
                      setShowConfirmPassword(
                        current => !current
                      )
                    }
                    className="
                      absolute
                      right-4
                      top-1/2
                      -translate-y-1/2
                      text-gray-400
                      hover:text-gray-600
                    "
                  >

                    {
                      showConfirmPassword
                        ? <FiEyeOff />
                        : <FiEye />
                    }

                  </button>

                </div>



                {error && (

                  <div className="
                    bg-red-50
                    border
                    border-red-100
                    text-red-600
                    rounded-xl
                    p-4
                    text-sm
                    mt-5
                  ">
                    {error}
                  </div>

                )}



                <button
                  type="submit"
                  disabled={
                    loading ||
                    !passwordValid
                  }
                  className={`
                    w-full
                    h-12
                    rounded-xl
                    text-white
                    font-bold
                    mt-6
                    transition

                    ${
                      loading ||
                      !passwordValid
                        ? `
                          bg-gray-300
                          cursor-not-allowed
                        `
                        : `
                          bg-green-600
                          hover:bg-green-700
                        `
                    }
                  `}
                >

                  {
                    loading
                      ? 'Changing password...'
                      : 'Change password'
                  }

                </button>

              </form>

            </>

          )}

        </div>

      </div>

    </div>
  )
}



function Requirement({
  valid,
  text
}) {

  return (

    <div className="
      flex
      items-center
      gap-2
      text-xs
    ">

      <div className={`
        w-4
        h-4
        rounded-full
        flex
        items-center
        justify-center

        ${
          valid
            ? `
              bg-green-100
              text-green-600
            `
            : `
              bg-gray-100
              text-gray-400
            `
        }
      `}>

        <FiCheck
          size={10}
        />

      </div>


      <span className={
        valid
          ? 'text-green-700'
          : 'text-gray-400'
      }>
        {text}
      </span>

    </div>
  )
}